/*----------------------------------
- LANDING/PRICING: FAQ CATALOG (SSOT)
----------------------------------*/

/**
 * @docs docs/features/website-faq
 *
 * Canonical FAQ list used by both:
 * - Landing page (`/`)
 * - Pricing page (`/pricing`)
 *
 * Keep this file as the single source of truth for FAQ copy + ordering.
 *
 * Rates and allowances are interpolated, never typed. These answers ship as
 * FAQPage JSON-LD on `/` and `/pricing`, so a stale number here is served to
 * assistants as a structured claim about what the product charges.
 *
 * Ordering lives here too, because it is per page and not per component.
 * `faqItems` is the pricing order: it opens on money, since a visitor who
 * reached `/pricing` is carrying a money objection and every product answer
 * before it is a delay. `HOME_FAQ_ITEMS` is a subset in a different order,
 * because a homepage visitor is not deciding whether to pay yet, and asking
 * them to read the refund policy first assumes a decision they have not made.
 *
 * Every answer states a fact that exists in the code or on a legal page. The
 * three that are promises rather than shipped behavior are marked below.
 */

import { ANALYSES_RESET_PHRASE } from '@uniquedomains/components/pricing/catalogs/analysesReset';
import { ANALYSIS_ACCESS_DAYS } from '@uniquedomains/core/domains/analysis';
import { PUBLIC_PLAN_FACTS } from '@uniquedomains/components/pricing/catalogs/pricingPlans';
import { FULL_ACCESS_RADARS } from '@uniquedomains/components/pricing/catalogs/radarOfferCopy';
import { PAYMENT_FAILURE_GRACE_LABEL } from '@uniquedomains/components/pricing/catalogs/paymentGrace';
import { REFUND_GUARANTEE_DAYS } from '@uniquedomains/components/pricing/catalogs/refundPromise';
import { AVAILABILITY_RUN_SIZE, WHOIS_PATH } from '@uniquedomains/components/contracts/lookup/limits';
import { REGISTRARS_PATH } from '@uniquedomains/components/contracts/registrars/public';
import { LEGAL_ENTITY } from '@/common/catalogs/legal/entity';
import { SUPPORT_CHAT_LINK } from '@/common/config/support';
import { ANALYSES_RESET_SENTENCE, PUBLIC_PREVIEW_SENTENCE } from '@/client/catalogs/landing/accessOffer';

/*----------------------------------
- TYPES
----------------------------------*/

export type FAQLink = {
    label: string;
    href: string;
};

export type FAQItem = {
    /**
     * Stable identity, so a page can pick and order answers without copying
     * their text. Optional because the compare, product and solutions pages
     * build their own unordered lists through the same component.
     */
    id?: string;
    /** Group label rendered above the first item of each run. Optional for the same reason. */
    topic?: string;
    question: string;
    answer: string;
    link?: FAQLink;
};

/** The shared catalog always carries both, so ordering and grouping cannot silently drop an answer. */
type SharedFAQItem = FAQItem & { id: string; topic: string };

/*----------------------------------
- FIGURES
----------------------------------*/

const MEMBER_FACTS = PUBLIC_PLAN_FACTS.member;

const MEMBER_PRICE = `$${MEMBER_FACTS.price}`;
const EXTRA_SEAT_PRICE = `$${MEMBER_FACTS.extraSeatPrice}`;

const formatCount = (value: number): string => new Intl.NumberFormat('en-US').format(value);

/*
 * The plan's limits as things (ADR-0083). The credit answers they replaced (the worked credit
 * month, the pack list, the rollover rule) went with the credits: members no longer see a credit
 * anywhere, so the FAQ states what the price covers instead of what a credit buys.
 *
 * The appraisal run is the checker's run size, which `limits.ts` documents as Appraise's sizes.
 */
const APPRAISAL_RUN_NAMES = AVAILABILITY_RUN_SIZE.fullAccess;

/** The member plan's per-export row ceiling, from the published facts. See the export answer below. */
const EXPORT_ROWS_CEILING = MEMBER_FACTS.exportRows;

/**
 * The number as a person reads it. `SUPPORT_CHAT_LINK` owns the digits, and the
 * contract test asserts these two carry the same ones, because a support number
 * that disagrees with its own link is worse than no number at all.
 */
export const SUPPORT_WHATSAPP_DISPLAY = '+33 6 01 71 15 55';

/** The FAQ's four groups, in the order the pricing page uses them. */
export const FAQ_TOPICS = {
    billing: 'Billing',
    limits: 'Plans and limits',
    product: 'How it works',
    company: 'The company',
} as const;

/*----------------------------------
- CATALOG
----------------------------------*/

const SHARED_FAQ_ITEMS: readonly SharedFAQItem[] = [
    /*----------------------------------
    - BILLING
    ----------------------------------*/

    {
        id: 'card-to-start',
        topic: FAQ_TOPICS.billing,
        question: 'Is there a free plan?',
        answer: `No, but you can look before you pay. ${PUBLIC_PREVIEW_SENTENCE} Full Access takes a card at checkout and charges ${MEMBER_PRICE} immediately. There is no trial, so the ${REFUND_GUARANTEE_DAYS}-day refund below is the safety net instead.`,
    },
    {
        id: 'what-am-i-charged',
        topic: FAQ_TOPICS.billing,
        question: 'What exactly am I charged, and when?',
        answer: `${MEMBER_PRICE} the moment you check out, then ${MEMBER_PRICE} on the same day each month until you cancel. Nothing else is charged unless you add a teammate: each extra seat is ${EXTRA_SEAT_PRICE} a month, from the day you add it. The exact recurring amount is shown on the plan card and again at checkout, before you commit.`,
    },
    {
        id: 'refund',
        topic: FAQ_TOPICS.billing,
        question: 'What if it turns out not to be for me?',
        answer: `Tell us within ${REFUND_GUARANTEE_DAYS} days of paying and we refund you in full. No conditions and no questions: it holds even if you already ran every analysis, appraisal or Radar. Email ${LEGAL_ENTITY.contactEmail} with the address on your account and the date of the payment. I answer within one working day, and the refund goes back to your card through Stripe, usually within 10 working days depending on your bank. One refund per customer under this guarantee. Consumers in the European Union also keep their 14-day right of withdrawal, and whichever gives you more is the one that applies.`,
        link: { label: 'Read the refund policy', href: '/refund-policy' },
    },
    {
        id: 'cancel',
        topic: FAQ_TOPICS.billing,
        question: 'Can I cancel?',
        answer: 'Yes, any time, from the billing portal in your settings. Cancelling stops the next renewal. Your plan stays active until the end of the period you already paid for. Nothing is deleted: your brief, Radars, Watchlists and reports stay saved, and open again if you subscribe again.',
    },
    {
        id: 'payment-failed',
        topic: FAQ_TOPICS.billing,
        question: 'What happens if a payment fails?',
        // `PAYMENT_FAILURE_GRACE_LABEL` (D23 of the paid-funnel plan): stated in hours, never in days,
        // so it cannot read as a trial.
        answer: `A refused payment is not a charge, so nothing is owed. When a renewal fails, Full Access stays open for ${PAYMENT_FAILURE_GRACE_LABEL} so you can update the card in the billing portal; after that the plan ends until the card goes through. Your data stays exactly where it is either way.`,
    },
    {
        id: 'annual-or-pause',
        topic: FAQ_TOPICS.billing,
        question: 'Is there an annual plan, or a way to pause?',
        answer: 'Not today. Full Access is monthly only, so there is no yearly commitment to sign and no discount for one. There is no pause button either: cancel, and subscribe again when the next decision comes. Your work stays where you left it in the meantime.',
    },
    {
        id: 'one-month-only',
        topic: FAQ_TOPICS.billing,
        question: 'What if I only need this once?',
        answer: `Take Full Access for the month the decision takes, and cancel before it renews. One month at ${MEMBER_PRICE}, no trial to remember, and nothing renews once you have cancelled.`,
    },
    {
        /*
         * A promise, not shipped behavior: nothing in the code enforces the
         * notice period. It is stated because the objection is real and the
         * answer is a commitment the company makes, not a feature it ships.
         */
        id: 'price-changes',
        topic: FAQ_TOPICS.billing,
        question: 'Will the price go up on me?',
        answer: 'The price you subscribe at is the price you keep for as long as your subscription stays active. When I retired the earlier plans, their subscribers moved onto Full Access and kept the price they had bought at, and the same rule applies to Full Access. Any change to the list price is announced by email before it could reach a renewal, so you can decide before it applies to you.',
    },
    {
        id: 'payment-and-invoices',
        topic: FAQ_TOPICS.billing,
        question: 'How do you take payment, and do I get invoices?',
        answer: 'By card, through Stripe. Your card details never reach Unique Domains. Any VAT or sales tax is worked out at checkout from your billing address rather than added afterwards, and every invoice is in the billing portal whenever you need it.',
    },

    /*----------------------------------
    - PLANS AND LIMITS
    ----------------------------------*/

    {
        id: 'limits',
        topic: FAQ_TOPICS.limits,
        question: 'What does Full Access include each month?',
        answer: `${MEMBER_FACTS.analysesPerMonth} full domain analyses a month, up to ${FULL_ACCESS_RADARS} Radars, appraisals of up to ${APPRAISAL_RUN_NAMES} names a run, CSV export of up to ${formatCount(EXPORT_ROWS_CEILING)} rows per export, and ${MEMBER_FACTS.dailyOpportunities} names a day in the daily edition with every past edition. One seat is included; each extra seat is ${EXTRA_SEAT_PRICE} a month. Searching, filtering, saving names and reopening a report you already ran never count. ${ANALYSES_RESET_SENTENCE}`,
    },
    {
        id: 'limit-reached',
        topic: FAQ_TOPICS.limits,
        question: 'What happens when I reach a limit?',
        answer: `Nothing is charged and nothing you saved disappears. Search, your lists and every report you already ran keep working. A new analysis waits until the count ${ANALYSES_RESET_PHRASE}, and a new Radar waits until you archive one. If a limit gets in the way of real work, tell me.`,
    },

    /*----------------------------------
    - HOW IT WORKS
    ----------------------------------*/

    {
        id: 'inside-an-analysis',
        topic: FAQ_TOPICS.product,
        question: 'What is in a full analysis?',
        answer: `One report per domain, built the moment you run it: a summary and business ideas, search demand, the fit with your Radar brief, an estimated value with the other extensions around the name, history, reputation and topical authority, a USPTO trademark check, and the current registration with timestamped DNS and registry record snapshots (read through RDAP or WHOIS, whichever the registry runs). Up to ten domains at a time, compared side by side. A report that delivers nothing does not count against your analyses, and every report stays open for ${ANALYSIS_ACCESS_DAYS} days.`,
    },
    {
        id: 'data-freshness',
        topic: FAQ_TOPICS.product,
        question: 'How fresh is the data?',
        // The free lookup tools (ADR-0078) read the same registry record live, one name a day for a
        // visitor, which is what makes the snapshot promise checkable without an account.
        answer: 'Availability and pricing are rescanned on a rolling schedule, and the words in most demand are rechecked most often. A full analysis reads the registry record and DNS again at the moment you run it, each stamped with the time it was checked, so you always know how old a fact is. The free WHOIS lookup and availability checker read one name live a day with no account. Registrars still change stock and prices by the hour, so confirm at the registrar before you buy.',
        link: { label: 'Look up a name in its registry', href: WHOIS_PATH },
    },
    {
        id: 'trademark',
        topic: FAQ_TOPICS.product,
        question: 'Do you guarantee trademark safety?',
        answer: 'No, and nobody honest can. The analysis runs a USPTO check so you catch the obvious conflicts before you fall for a name, and it links you straight to the record. It is a signal, not legal advice: confirm with counsel before you commit to a brand.',
    },
    {
        id: 'commissions',
        topic: FAQ_TOPICS.product,
        question: 'Do you sell domains, or earn commissions on them?',
        answer: 'Neither. Unique Domains does not sell or register domains. The product routes you to registrars and marketplaces, and the purchase happens there, at their price. Today it earns nothing from those links, so no recommendation you see is nudged by a payout. If that ever changes, it will be stated here first.',
        // The registrar hub (ADR-0079) compares those prices in public, which this answer can point at.
        link: { label: 'Compare registrar prices', href: REGISTRARS_PATH },
    },
    {
        id: 'why-not-spreadsheet',
        topic: FAQ_TOPICS.product,
        question: "Can't I do this with registrars, marketplaces and a spreadsheet?",
        answer: 'You can, for one shortlist on one evening. What breaks is the second week: a spreadsheet cannot recheck availability, notice a price drop, run a trademark check, or remember why one name beat another. Unique Domains keeps the search, the evidence and the follow-up in one place, so the decision survives being interrupted.',
    },
    {
        id: 'not-a-generator',
        topic: FAQ_TOPICS.product,
        question: 'Is this just a naming generator?',
        answer: 'No. Generators invent names that may not exist. I start from domains that are actually registerable or for sale, and score them against your brief. Use it when you want a buyable shortlist you can defend, not endless inspiration.',
    },
    {
        id: 'why-one-word',
        topic: FAQ_TOPICS.product,
        question: 'Why only one-word domains?',
        answer: 'Because one word is the tier where a name is memorable, brandable and comparable, and comparability is what makes a price defensible. Going deep on one tier lets me build real word intelligence and market evidence, instead of a thin layer over everything.',
        link: {
            label: 'Browse brandable one-word domains',
            href: '/domains/brandable',
        },
    },
    {
        id: 'radar-notifications',
        topic: FAQ_TOPICS.product,
        question: 'How does a Radar notify me?',
        answer: `A Radar is your saved search criteria. It runs every day, and each run drops the new matches and changes into your workspace with a briefing attached. A digest email summarises what changed, one message rather than a stream of alerts. Full Access runs up to ${FULL_ACCESS_RADARS} Radars, briefings included.`,
        link: {
            label: 'See how Radars work',
            href: '/product/radars',
        },
    },
    {
        id: 'export',
        topic: FAQ_TOPICS.product,
        question: 'Can I export my results?',
        // The ceiling comes from the published facts, which a contract test holds to the Product
        // plan config (`exportRowsCount.value`). Never metered since ADR-0083.
        answer: `Yes, on Full Access. Export any selection as CSV, up to ${formatCount(EXPORT_ROWS_CEILING)} rows per export. Any analysis can also be shared by link with someone who has no account.`,
    },
    {
        id: 'api',
        topic: FAQ_TOPICS.product,
        question: 'Do you offer an API?',
        answer: 'Not yet, on any plan: the customer-facing REST API is still in development. Its documentation and sample responses are published so you can plan an integration before it opens.',
        link: {
            label: 'Review current API status',
            href: '/api',
        },
    },
    {
        id: 'team-and-clients',
        topic: FAQ_TOPICS.product,
        question: 'Can my team use it, or can I use it for clients?',
        answer: `Full Access includes one seat, and each teammate after it is ${EXTRA_SEAT_PRICE} a month. Client work is welcome: agencies and studios run a Radar per client brief on it, and any analysis can be shared by link with someone who has no account.`,
    },

    /*----------------------------------
    - THE COMPANY
    ----------------------------------*/

    {
        id: 'who-is-behind',
        topic: FAQ_TOPICS.company,
        question: 'Who is behind Unique Domains?',
        answer: `One person, and a company you can look up. Unique Domains is built by ${LEGAL_ENTITY.name}, a French company registered in Paris under ${LEGAL_ENTITY.registrationNumber}, and run by its founder Kai Etan from Singapore. EU consumer law and EU privacy rules therefore apply to every customer, wherever you are.`,
        link: { label: 'Meet the founder', href: '/team' },
    },
    {
        id: 'data-after-cancel',
        topic: FAQ_TOPICS.company,
        question: 'What happens to my data if I cancel or delete my account?',
        answer: 'Cancelling deletes nothing. Ask us to delete your account and we erase it from our database, our email tool, our analytics and our payment provider in one go. Two things stay: the invoices tax law requires the payment provider to keep, and internal notifications we already received about your account. You can also ask for a copy of your data at any time, and we answer within one month.',
        link: { label: 'Read the privacy policy', href: '/privacy' },
    },
    {
        id: 'reach-a-human',
        topic: FAQ_TOPICS.company,
        question: 'How do I reach a human, and how fast?',
        answer: `Three ways, and all three reach a person: email ${LEGAL_ENTITY.contactEmail}, WhatsApp on ${SUPPORT_WHATSAPP_DISPLAY}, or the chat bubble at the bottom right of this page. No AI bot sits on any of them and there is no ticket queue: you are talking to me. I answer within one working day, and refund requests are handled in the same window.`,
        link: { label: 'Message me on WhatsApp', href: SUPPORT_CHAT_LINK },
    },
    {
        /*
         * A promise, not shipped behavior, like `price-changes`. Stated because
         * a one-person company is the objection nobody says out loud, and an
         * unanswered one costs more than the commitment does.
         */
        id: 'if-you-shut-down',
        topic: FAQ_TOPICS.company,
        question: 'What if Unique Domains shuts down?',
        answer: 'You keep your work and your money. Every list and report can be exported as CSV, and you would be refunded for any period you had paid for and not received. One person, but a registered company with a published address and a named director, not an anonymous side project.',
    },
] as const;

/*----------------------------------
- ORDERING
----------------------------------*/

const FAQ_ITEMS_BY_ID = new Map(SHARED_FAQ_ITEMS.map((item) => [item.id, item]));

/**
 * Throws on an unknown id rather than dropping the answer, so a rename fails at
 * boot and in the contract test instead of quietly serving a page one objection
 * short.
 */
const pickFaqItems = (ids: readonly string[]): readonly FAQItem[] =>
    ids.map((id) => {
        const item = FAQ_ITEMS_BY_ID.get(id);
        if (!item) throw new Error(`[faq] Unknown FAQ id: ${id}`);
        return item;
    });

/** Every answer, in the order `/pricing` asks them. */
export const faqItems: readonly FAQItem[] = SHARED_FAQ_ITEMS;

/**
 * The homepage subset. It drops the eleven answers that only matter once
 * somebody is deciding whether to pay (what exactly am I charged, failed
 * payments, annual billing, invoices, and so on), and reorders what is left so
 * the page opens on what the product is rather than on what it costs.
 */
export const HOME_FAQ_ITEMS: readonly FAQItem[] = pickFaqItems([
    'why-not-spreadsheet',
    'not-a-generator',
    'why-one-word',
    'inside-an-analysis',
    'data-freshness',
    'radar-notifications',
    'trademark',
    'commissions',
    'card-to-start',
    'limits',
    'limit-reached',
    'refund',
    'cancel',
    'who-is-behind',
    'reach-a-human',
]);
